import { useQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import { createChatbot } from './createChatbot';
import type { ChatbotResponse, GetChatbotParams } from './types';

export async function getChatbot({ artistId }: GetChatbotParams): Promise<ChatbotResponse> {
  if (!artistId) throw Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/chatbot`;
  try {
    const response = await api.executeGetRequest<ChatbotResponse>(url);
    return response.data;
  } catch (error) {
    // Check if it's a 404 error - use optional chaining for safety
    const is404 = error?.response?.status === 404 || error?.status === 404;

    if (is404) {
      // Create the chatbot if it doesn't exist
      await createChatbot({ artistId });
      // Fetch and return the newly created chatbot
      const response = await api.executeGetRequest<ChatbotResponse>(url);
      return response.data;
    }

    throw error;
  }
}

export function useChatbot(artistId: string) {
  const user = storage.getUser();

  const query = useQuery({
    enabled: !!artistId && !!user?.is_registered,
    queryKey: queryKeys.artistChatbot(artistId),
    queryFn: () => getChatbot({ artistId }),
    retry: false,
  });

  return query;
}
